// chat/actions.ts — the chat control EXECUTOR (Expense-pattern, adapted to THIS store).
// The client router (chat/brain.ts) maps ONE user message → ONE ChatAction; this file
// EXECUTES that action against the on-device store (src/store.ts). Every mutating call is
// LOCAL SQLite over Tauri IPC — no network, no billing.
//
// The billed steps (recap · ask) reuse the app's EXISTING live endpoints via
// store.computeSignals + brain.weeklyRecap/askHealth — no new/edited server route, ONE
// billed call each, medical-fenced pre-call.
//
// HITL discipline (mirrors Expense): the destructive action (delete_meal) is NEVER executed
// from a chat turn on its own — the Chat UI holds it and requires an explicit Confirm tap.

import { store, todayLocal, type Meal, type MealPatch, type MealSource } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, estimateMeal } from "../brain";
import { saveCopyNow, importFromFile } from "../localFolder";

const LOCAL_SLUG = "meal-tracker"; // friendly filename stem for the on-device backup file
import { addInstruction } from "./brain";
import { weeklyRecap, askHealth, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact meal row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  name: string;
  eaten_on: string;
  calories: number;
  source: MealSource;
}

// A purely LOCAL filter over the user's own log (no server route — search is computed
// on-device from listMeals).
export interface MealFilter {
  /** Free-text contains over name / note. */
  text?: string;
  /** Exact YYYY-MM-DD day. */
  date?: string;
  source?: MealSource;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_meal"; name: string; calories?: number; protein_g?: number; eaten_on?: string }
  | { kind: "add_weight"; weight_kg: number }
  | { kind: "edit_meal"; id: string; patch: MealPatch; label?: string }
  | { kind: "delete_meal"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; filter: MealFilter; label?: string }
  | { kind: "recap" }
  | { kind: "ask"; question: string }
  | { kind: "estimate_meal"; description: string }
  | { kind: "delete_weight"; date: string }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before this reaches executeAction (replaces all data)
  | { kind: "save_local_copy" }  // iPhone/Mac: save a one-off copy to a folder/location the user picks
  | { kind: "import_file" }      // import/restore from a file the user picks (REPLACES data — UI confirms first)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // the log changed → the main list should refresh
  rows?: FilterRow[]; // filter matches to render inline
}

const norm = (s: string) => s.trim().toLowerCase();

/** Apply a MealFilter locally — every condition ANDs; empty filter matches all. */
export function applyMealFilter(meals: Meal[], f: MealFilter): Meal[] {
  return meals.filter((m) => {
    if (f.date && m.eaten_on !== f.date) return false;
    if (f.source && m.source !== f.source) return false;
    if (f.text) {
      const t = norm(f.text);
      const hit = m.name.toLowerCase().includes(t) || m.note.toLowerCase().includes(t);
      if (!hit) return false;
    }
    return true;
  });
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_meal": {
        // THE ESTIMATOR, end to end: no numbers given → the AI estimates them. A meal must
        // NEVER be stored as 0 kcal just because the human didn't know (G12 catch 2026-07-06:
        // "two boiled eggs and a slice of toast · ~0 kcal est.").
        let calories = a.calories ?? 0;
        let protein_g = a.protein_g ?? null;
        let carbs_g: number | null = null;
        let fat_g: number | null = null;
        let estimated = false;
        if (!calories) {
          try {
            const est = await estimateMeal(a.name);
            if (est.calories) {
              calories = est.calories;
              protein_g = protein_g ?? est.protein_g;
              carbs_g = est.carbs_g;
              fat_g = est.fat_g;
              estimated = true;
            }
          } catch { /* estimator unavailable → log without numbers, editable any time */ }
        }
        const m = await store.createMeal({
          name: a.name,
          eaten_on: a.eaten_on ?? todayLocal(),
          calories,
          protein_g,
          carbs_g,
          fat_g,
          source: "manual",
        });
        const tail = m.calories > 0
          ? ` · ~${m.calories} kcal${estimated ? " (AI estimate — edit any time)" : " est."}`
          : " · no calorie estimate yet (edit it any time)";
        return { ok: true, message: `Logged "${m.name}" for ${m.eaten_on}${tail}`, changed: true };
      }

      case "add_weight": {
        const w = await store.upsertWeight({ weight_kg: a.weight_kg, measured_on: todayLocal() });
        return { ok: true, message: `Logged ${w.weight_kg} kg for ${w.measured_on}.`, changed: true };
      }

      case "edit_meal":
        await store.updateMeal(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the meal"}.`, changed: true };

      case "delete_meal":
        await store.deleteMeal(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the meal"}.`, changed: true };

      case "filter": {
        const all = await store.listMeals({ limit: 50000 });
        const matches = applyMealFilter(all, a.filter);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rows: FilterRow[] = matches.slice(0, 50).map((m) => ({
          id: m.id,
          name: m.name,
          eaten_on: m.eaten_on,
          calories: m.calories,
          source: m.source,
        }));
        return {
          ok: true,
          message: `${matches.length} meal${matches.length !== 1 ? "s" : ""}${forLabel}` + (matches.length > 50 ? " (showing first 50)" : ""),
          rows,
        };
      }

      case "recap": {
        // The billed step — reuses the app's EXISTING live endpoint. computeSignals runs
        // on-device; weeklyRecap bills the Oriora wallet. Errors (BrainError) caught below.
        const sig = await store.computeSignals(todayLocal());
        const r = await weeklyRecap(sig);
        return { ok: true, message: r.recap };
      }

      case "ask": {
        // Billed; askHealth enforces the medical fence + empty-log guard pre-call.
        const sig = await store.computeSignals(todayLocal());
        const r = await askHealth(sig, a.question);
        return { ok: true, message: r.answer };
      }

      case "estimate_meal": {
        const est = await estimateMeal(a.description);
        const parts = [
          `~${est.calories ?? "?"} kcal`,
          est.protein_g != null ? `${est.protein_g}p` : null,
          est.carbs_g != null ? `${est.carbs_g}c` : null,
          est.fat_g != null ? `${est.fat_g}f` : null,
        ].filter(Boolean).join(" · ");
        return { ok: true, message: `Estimate for ${est.name || a.description}: ${parts} (${est.confidence ?? "rough"} confidence — these are rough figures). Say "log it" and I'll add it.` };
      }

      case "delete_weight": {
        const w = (await store.listWeights()).find((x) => x.measured_on === a.date);
        if (!w) return { ok: false, message: `No weight logged on ${a.date}.` };
        await store.deleteWeight(w.id);
        return { ok: true, message: `Deleted the weight entry for ${a.date}.`, changed: true };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: `Backed up your whole log to your ${name} — restore it on any device, any time.` };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my log" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up my log" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored your log from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        // The READ side of "your data outlives the app": bring a backup file back IN from anywhere the
        // user has it (Files, another device, a folder). importBackup validates the envelope and REPLACES
        // current data — so the Chat UI holds this for an explicit Confirm before it reaches here.
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json); // throws on a bad/foreign file → caught below as a friendly error
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole food+weight log). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: `Noted — from now on: ${a.instruction}` };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
